import { CreateShapeOptions, ShapesGroupId } from '../../../lib/charting_library';
import TradingView from '../../trading-view';

interface ShapePoint {
  price: number;
  time: number;
  order: number;
}

const horizontalLineStyles = {
  linecolor: '#0A84FF',
  linewidth: 1,
  linestyle: 4,
} as const;

const triangleFillStyles = {
  color: 'rgba(0, 0, 0, 0)',
  backgroundColor: 'rgba(10, 132, 255, 0.16)',
  linewidth: 0,
} as const;

const triangleBorderStyles = {
  linecolor: '#1F66A8',
  linewidth: 1,
} as const;

export class TrianglePatternHelper {
  #patternId: ShapesGroupId | null = null;

  constructor(private readonly tv: TradingView) {}

  draw(points: ShapePoint[]) {
    const chart = this.tv.activeChart;
    if (!chart || points.length < 3) return;
    this.clean();

    const sortedByTimePoints = [...points].sort((a, b) => a.time - b.time);
    const sortedByOrderPoints = [...points].sort((a, b) => a.order - b.order);

    const firstOrderPoint = sortedByOrderPoints[0];
    const latestTimePoint = sortedByTimePoints[sortedByTimePoints.length - 1];
    const lastOrderPoint = sortedByOrderPoints[sortedByOrderPoints.length - 1];

    const generateDrawingPoints = () => {
      // time 값이 chart candle의 interval 단위와 맞지 않으면 drawing의 좌표가 틀어지게 된다.
      // 따라서 모든 time 값을 endOfPeriodToBarTime 함수를 통해 chart candle의 interval 단위로 변환한다.
      return {
        horizontalLinePoints: [firstOrderPoint, latestTimePoint, lastOrderPoint].map((p) => ({
          time: chart.endOfPeriodToBarTime(p.time),
          price: p.price,
        })),
        triangleFillPoints: sortedByOrderPoints.map((p) => ({
          time: chart.endOfPeriodToBarTime(p.time),
          price: p.price,
        })),
        upperSidePoints: [firstOrderPoint, latestTimePoint].map((p) => ({
          time: chart.endOfPeriodToBarTime(p.time),
          price: p.price,
        })),
        lowerSidePoints: [latestTimePoint, lastOrderPoint].map((p) => ({
          time: chart.endOfPeriodToBarTime(p.time),
          price: p.price,
        })),
      };
    };

    const { horizontalLinePoints, triangleFillPoints, upperSidePoints, lowerSidePoints } = generateDrawingPoints();

    const horizontalLines = horizontalLinePoints.map((point) =>
      chart.createShape(point, {
        shape: 'horizontal_ray' as CreateShapeOptions<{}>['shape'],
        zOrder: 'bottom',
        lock: true,
        overrides: horizontalLineStyles,
      }),
    );

    const triangleFillId = chart.createMultipointShape(triangleFillPoints, {
      shape: 'polyline',
      zOrder: 'bottom',
      lock: true,
      overrides: triangleFillStyles,
    });

    const upperSideLineId = chart.createMultipointShape(upperSidePoints, {
      shape: 'polyline',
      zOrder: 'bottom',
      lock: true,
      overrides: triangleBorderStyles,
    });

    const lowerSideLineId = chart.createMultipointShape(lowerSidePoints, {
      shape: 'polyline',
      zOrder: 'bottom',
      lock: true,
      overrides: triangleBorderStyles,
    });

    const allShapes = [triangleFillId, upperSideLineId, lowerSideLineId, ...horizontalLines].filter((i) => i != null);

    try {
      chart.selection().add(allShapes);
      const groupId = chart.shapesGroupController().createGroupFromSelection();
      chart.shapesGroupController().groupLock(groupId);
      chart.selection().clear();
      this.#patternId = groupId;
      return groupId;
    } catch {
      return undefined;
    }
  }

  clean() {
    const chart = this.tv.activeChart;
    if (!chart || !this.#patternId) return;
    chart.shapesGroupController().removeGroup(this.#patternId);
    this.#patternId = null;
  }
}

export interface PointStats {
  oldestTime: number;
  newestTime: number;
  highestPrice: number;
  lowestPrice: number;
}

export const calculatePointStats = (points: ShapePoint[]): PointStats => {
  if (points.length === 0) return { oldestTime: 0, newestTime: 0, highestPrice: 0, lowestPrice: 0 };

  return {
    oldestTime: Math.min(...points.map((p) => p.time)),
    newestTime: Math.max(...points.map((p) => p.time)),
    highestPrice: Math.max(...points.map((p) => p.price)),
    lowestPrice: Math.min(...points.map((p) => p.price)),
  };
};

export const clearAllPatterns = (tv: TradingView) => {
  if (!tv?.activeChart) return;
  tv.activeChart?.removeAllShapes();
  tv.activeChart
    .shapesGroupController()
    .groups()
    .forEach((group) => {
      tv.activeChart?.shapesGroupController().removeGroup(group);
    });
};
